import { throttle } from './throttle';
import { scrollInterval, numScreensLazyLoad } from './config';
import { isIntersectionObserverSupported } from '@flipp/shopper-ad-web-shared';

/**
 * Generate a scrollHandler with throttled checking for whether an element is in view
 *
 * @param el The element to check if in viewport
 * @param cb The callback to execute when in viewport
 * @returns The throttled scroll handler
 */
const handleScrollLazyLoad = function (el: Readonly<Element>, cb: () => void) {
  // Return a handler that will check if in viewport at most every `scrollInterval` ms
  const listener = throttle(() => {
    if (isInView(el.getBoundingClientRect())) {
      cb();
      window.removeEventListener('scroll', listener);
      return null;
    }
    return null;
  }, scrollInterval);
  return listener;
};

/**
 * Fire the callback when the element first comes within X screens of the viewport
 *
 * @param el - Element to check if in viewport
 * @param cb - Callback to fire when in viewport
 * @param rootElement - root element to check the visibility
 * @param lazyThreshold - Number of screens away to fire callback
 */
export const fireOnFirstInView = function (
  el: Readonly<Element>,
  cb: () => null,
  rootElement: string | null = null,
  lazyThreshold = numScreensLazyLoad,
) {
  // Prefer intersection observer if supported but fallback to scroll observer if not
  if (isIntersectionObserverSupported()) {
    const observerConfig: IntersectionObserverInit = {
      // Look at the viewport + X% on either side in the vertical direction
      rootMargin: `${lazyThreshold * 100}% 0px`,
      root: rootElement ? document.querySelector(rootElement) : null,
    };

    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        cb();
        observer.disconnect();
      }
    }, observerConfig);
    observer.observe(el);
  } else {
    // If already in view fire immediately
    if (isInView(el.getBoundingClientRect())) {
      cb();
      return;
    }

    window.addEventListener('scroll', handleScrollLazyLoad(el, cb), {
      passive: true,
    });
  }
};

/**
 * Checks whether an element is in viewport
 * NOTE: This is not safe in nested iframes. It will almost always return true.
 *
 * @param rect The DOMRect of the element's bounding box to check if in viewport
 * @param threshold The number of screens away to fire callback (Optional: Defaults to config.ts value)
 * @returns True if in viewport
 */
export const isInView = function (
  rect: DOMRectReadOnly,
  threshold = numScreensLazyLoad,
) {
  // Want to load when X screens away
  if (rect.top >= 0) {
    // Scrolling from top to bottom
    return rect.top - window.innerHeight < window.innerHeight * threshold;
  } else {
    // Scrolling from bottom to top
    return Math.abs(rect.bottom) < window.innerHeight * threshold;
  }
};
